import { useState } from 'react';
import { Link } from 'react-router-dom';
import { arcHref } from '../routes/ArcRoute';
import type { BoardRow } from '../plan/board';
import { childEntries, shelvesOf } from '../plan/grouping';
import type { Group } from '../plan/grouping';
import { ArcCard } from './ArcCard';
import { listOf } from '../entity/entity';
import { tagMarkOf } from '../folders/TagMark';
import { StatusGlyph, StorylineGlyph } from './StatusGlyph';
import type { CardHolderProps } from './cardProps';

/**
 * The active band, with horizontal space spent on GROUPING.
 *
 * A shelf head is furniture, not an entity: it adds vertical structure and no
 * horizontal inset, so a member card is the same width as a loose one and
 * wraps its text identically. With no storylines the layout IS the wall —
 * which is why the `on their own` head appears only once a shelf exists above
 * it to be distinguished from.
 *
 * The head counts everything the storyline holds at any depth while the grid
 * shows direct children; counting what renders would under-report a nested
 * storyline, which this board has already been wrong about once.
 */

const CLOSED_KEY = 'plan.shelvesClosed';

/**
 * The loose band's key in the same closed set the shelves use.
 *
 * Reserved rather than a name, because the set is keyed by arc name and an arc could in principle
 * be called anything — a colon-led sentinel is one no filename produces.
 */
const LOOSE_KEY = ':on-their-own';

function readClosed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(CLOSED_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

export function Shelves({ rows, ...card }: { rows: BoardRow[] } & CardHolderProps) {
  const [closed, setClosed] = useState<Set<string>>(readClosed);
  const { shelves, loose } = shelvesOf(rows);

  const toggle = (name: string) => {
    const next = new Set(closed);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    localStorage.setItem(CLOSED_KEY, JSON.stringify([...next]));
    setClosed(next);
  };

  return (
    <div className="shelves">
      {shelves.map((shelf) => {
        const open = !closed.has(shelf.holder.arc.name);
        return (
          <section key={shelf.holder.arc.path} className="shelf">
            <ShelfHead
              group={shelf}
              open={open}
              onToggle={() => toggle(shelf.holder.arc.name)}
              drag={card.drag}
            />
            {open && <Wall groups={childEntries(shelf)} {...card} />}
          </section>
        );
      })}

      {loose.length > 0 && (() => {
        // Collapsible only when a shelf stands above it. With no storylines the loose wall IS the
        // board, and a head that can hide the whole of it is a trapdoor rather than furniture —
        // which is the same reason the head itself only appears once there is something to be
        // distinguished from.
        const headed = shelves.length > 0;
        const open = !headed || !closed.has(LOOSE_KEY);
        return (
          <section className="shelf">
            {headed && (
              <div className="shelf-head is-label">
                <button
                  type="button"
                  className={`chev${open ? ' down' : ''}`}
                  onClick={() => toggle(LOOSE_KEY)}
                  title={open ? 'Collapse' : 'Expand'}
                >
                  ›
                </button>
                <span className="t-eyebrowMono shelf-loose">on their own</span>
                <span className="t-metaMono band-count">{loose.length}</span>
                <span className="band-rule" />
              </div>
            )}
            {open && <Wall groups={loose} {...card} />}
          </section>
        );
      })()}
    </div>
  );
}

/**
 * The head names a storyline, and a storyline is an arc with a page of its
 * own — so the title opens it and only the chevron collapses the shelf. A
 * head that swallowed the click left its own arc unreachable.
 */
function ShelfHead({
  group,
  open,
  onToggle,
  drag,
}: {
  group: Group;
  open: boolean;
  onToggle: () => void;
  drag?: CardHolderProps['drag'];
}) {
  const arc = group.holder.arc;
  return (
    <div
      className="shelf-head"
      // A storyline is an arc, so the wall has to be able to reorder it — the
      // head is the only thing standing for it here, since a shelf renders its
      // members rather than a card of its own.
      data-arc-name={drag ? arc.name : undefined}
      onPointerDown={drag?.onCardPointerDown(arc.name)}
    >
      <button
        type="button"
        className={`chev${open ? ' down' : ''}`}
        onClick={onToggle}
        title={open ? 'Collapse' : 'Expand'}
      >
        ›
      </button>
      <StatusGlyph status={arc.status} />
      <StorylineGlyph size={14} />
      <Link to={arcHref(arc.name)} className="shelf-title">
        {arc.title}
      </Link>
      <span className="t-metaMono band-count">{group.members.length} inside</span>
      <span className="band-rule" onClick={onToggle} />
    </div>
  );
}

/** The card grid. Members and loose arcs share it — a member is not lesser
 *  than an arc that happens to sit at top level. */
export function Wall({
  groups,
  registries,
  theme,
  hold,
  tintOf,
  drag,
  focused,
  selected,
  onPick,
  onMenu,
  onToggleSelect,
}: { groups: Group[] } & CardHolderProps) {
  return (
    <div className="wall">
      {groups.map(({ holder, members }) => (
        <div
          key={holder.arc.path}
          className="wall-slot"
          onClickCapture={(event) => {
            // Shift-click extends the selection instead of following the
            // title's link — the modifier is what separates the two.
            if (!event.shiftKey || holder.depth > 0) return;
            event.preventDefault();
            event.stopPropagation();
            onToggleSelect?.(holder.arc.name, true);
          }}
        >
          <ArcCard
            arc={holder.arc}
            members={members}
            tint={tintOf(holder.arc, registries, theme) ?? undefined}
            mark={tagMarkOf(listOf(holder.arc.frontMatter['tags']), registries.folders, theme)}
            plain={hold === 'none'}
            focused={focused === holder.arc.name}
            selected={selected?.has(holder.arc.name) ?? false}
            dragging={drag?.draggingName === holder.arc.name}
            dropBefore={
              drag?.dropTarget?.beforeName === holder.arc.name && drag?.draggingName !== null
            }
            movable={Boolean(drag)}
            onPointerDown={drag?.onCardPointerDown(holder.arc.name)}
            onOpenPicker={(anchor) => onPick(holder.arc, anchor)}
            onMenu={onMenu}
          />
        </div>
      ))}
    </div>
  );
}
